import type { FC } from "react";

import { LoyaltyPointsStatus as LoyaltyPointsStatusLib } from "@jti/ui/components/LoyaltyPointsStatus";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { formatDate } from "@jti/ui/utils/formatDate/formatDate";

import { useLoyaltyPointsStatusLogic } from "./useLoyaltyPointsStatusLogic";

export type LoyaltyPointsStatusProps = {
  tiers: Record<string, string>;
  pointsHistoryLabel: string;
  pointsHistoryUrl: string;
  ctaButtonLabel?: string;
  ctaButtonUrl?: string;
  secondaryCtaButtonLabel?: string;
  secondaryCtaButtonUrl?: string;
  isInEditor?: boolean;
  hideBottomSection?: boolean;
};

export const LoyaltyPointsStatus: FC<LoyaltyPointsStatusProps> = ({
  pointsHistoryLabel,
  pointsHistoryUrl,
  secondaryCtaButtonLabel,
  secondaryCtaButtonUrl,
  tiers,
  ctaButtonLabel,
  ctaButtonUrl,
  isInEditor,
  hideBottomSection,
}) => {
  const { __ } = useTranslations();

  const {
    isHidden,
    isLoading,
    userTierName,
    userTierToLowerCase,
    balance,
    pointsToExpire,
    expireAt,
    cumulativePoints,
    securedTillDate,
    progressBarPointsForGoldTier,
    progressBarPointsForBlackTier,
    tiersInfo,
    tiersNames,
    detailsBarRightTitle,
    currentTierAssignmentYear,
  } = useLoyaltyPointsStatusLogic();

  if (isHidden && !isInEditor) {
    return null;
  }

  const detailsBarRightDescription = pointsToExpire ? (
    <>
      {__("loyalty.pointsStatus.upperItemDetailsBarRightDescription", [pointsToExpire])}{" "}
      <b style={{ fontWeight: "bold" }}>{formatDate(expireAt)}</b>
    </>
  ) : null;

  return (
    <LoyaltyPointsStatusLib>
      <LoyaltyPointsStatusLib.UpperItem
        points={balance}
        title={__("loyalty.pointsStatus.upperItemTitle")}
        description={__("loyalty.pointsStatus.upperItemDescription")}
        displayBarLeftDescription={__("loyalty.pointsStatus.upperItemDisplayBarLeftDescription")}
        detailsBarRightDescription={detailsBarRightDescription}
        pointsLabel={__("loyalty.pointsStatus.upperItemPointsLabel")}
        pointsHistoryLabel={pointsHistoryLabel}
        pointsHistoryUrl={pointsHistoryUrl}
        detailsBarButtonLabel={ctaButtonLabel}
        detailsBarButtonUrl={ctaButtonUrl}
        isLoading={isLoading}
      />

      {!hideBottomSection && (
        <LoyaltyPointsStatusLib.BottomItem
          cumulativePoints={cumulativePoints}
          progressBarPointsForGoldTier={progressBarPointsForGoldTier}
          progressBarPointsForBlackTier={progressBarPointsForBlackTier}
          progressBarPointsTitle={__("loyalty.pointsStatus.progressBarPointsTitle", [new Date().getFullYear()])}
          progressBarPointsFooter={__("loyalty.pointsStatus.progressBarPointsFooter")}
          userTier={userTierName}
          date={securedTillDate}
          tiersInfo={tiersInfo}
          tierImage={tiers[userTierToLowerCase]}
          title={__("loyalty.pointsStatus.bottomItemTitle", [userTierName])}
          description={__("loyalty.pointsStatus.bottomItemDescription")}
          displayBarLeftDescription={__("loyalty.pointsStatus.bottomItemDisplayBarLeftDescription")}
          detailsBarRightDescription={__("loyalty.pointsStatus.bottomItemDetailsBarRightDescription", [
            userTierName,
            currentTierAssignmentYear,
          ])}
          detailsBarRightTitle={detailsBarRightTitle}
          detailsBarGeneralTitle={__("loyalty.pointsStatus.bottomItemDetailsBarGeneralTitle")}
          detailsBarButtonLabel={secondaryCtaButtonLabel}
          detailsBarButtonUrl={secondaryCtaButtonUrl}
          isLoading={isLoading}
          tiersNames={tiersNames}
        />
      )}
    </LoyaltyPointsStatusLib>
  );
};
